import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { apiUrl, currentUserId, currentUserQuery } from "./auth";

export type CrmRole = "lecture" | "commercial" | "admin";
export type CrmMember = { id: number; name: string; role: CrmRole };

type Bag = { role: CrmRole; canWrite: boolean; isAdmin: boolean; members: CrmMember[]; refresh: () => Promise<void> };
const Access = createContext<Bag>({ role: "admin", canWrite: true, isAdmin: true, members: [], refresh: async () => undefined });

export function AccessScope({ children }: { children: ReactNode }) {
  const [role, setRole] = useState<CrmRole>("admin");
  const [members, setMembers] = useState<CrmMember[]>([]);

  async function refresh() {
    const headers = new Headers();
    const userId = currentUserId();
    if (userId) headers.set("X-User-Id", userId);
    const res = await fetch(apiUrl("roles.php") + currentUserQuery(), { credentials: "include", cache: "no-store", headers });
    const data = (await res.json().catch(() => ({}))) as { success?: boolean; role?: CrmRole; members?: CrmMember[] };
    if (!res.ok || data.success === false) return;
    if (data.role === "lecture" || data.role === "commercial" || data.role === "admin") setRole(data.role);
    if (Array.isArray(data.members)) setMembers(data.members);
  }

  useEffect(() => { void refresh(); }, []);

  const bag: Bag = { role, canWrite: role !== "lecture", isAdmin: role === "admin", members, refresh };
  return <Access.Provider value={bag}>{children}</Access.Provider>;
}

export function useAccess(): Bag {
  return useContext(Access);
}

export async function saveMemberRole(userId: number, role: CrmRole): Promise<CrmMember[]> {
  const headers = new Headers({ "Content-Type": "application/json" });
  const me = currentUserId();
  if (me) headers.set("X-User-Id", me);
  const res = await fetch(apiUrl("roles.php") + currentUserQuery(), {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ user_id: userId, role }),
  });
  const data = (await res.json().catch(() => ({}))) as { success?: boolean; message?: string; members?: CrmMember[] };
  if (!res.ok || data.success === false || !data.members) throw new Error(data.message || "Rôle non enregistré");
  return data.members;
}

export function AccessNote() {
  const { role } = useAccess();
  if (role === "admin") return null;
  return <span className="crm-pill rounded-full px-3 py-1 text-xs font-semibold">{role === "lecture" ? "Lecture seule" : "Commercial"}</span>;
}
